/**
 * Position calibration
 *
 * Which template of a position counts as its default, and whether the
 * position is configured (R31): its print area carries both millimetre
 * values and that default template is calibrated. A product is selectable
 * in the Creator and in Direct Sale only when its default position is
 * configured (R13). The server read path, the legacy mirror and the
 * clients all decide this with the functions below, so the rule exists once.
 */
import {
  isCalibrated,
  isFinitePositive,
  type CalibrationFields,
  type PrintAreaMmInput,
} from './templateGeometry.js';

/** The template fields the default rule needs. */
export interface DefaultTemplateCandidate {
  isDefault: boolean;
  createdAt: Date | string;
}

export interface EffectiveDefaultTemplate<T> {
  /** The template that counts as the position's default, or null. */
  template: T | null;
  /**
   * Further templates flagged as default that lost against the oldest one.
   * Not empty means the catalogue is inconsistent and worth a finding.
   */
  ignored: T[];
}

function createdAtMs(value: Date | string): number {
  return value instanceof Date ? value.getTime() : Date.parse(value);
}

/**
 * The default template of one position's templates. When several are
 * flagged as default the oldest one (by `createdAt`) counts; the others are
 * returned in `ignored` so the caller can log them.
 */
export function effectiveDefaultTemplate<T extends DefaultTemplateCandidate>(
  templates: readonly T[]
): EffectiveDefaultTemplate<T> {
  const defaults = templates
    .filter((template) => template.isDefault)
    .sort((a, b) => createdAtMs(a.createdAt) - createdAtMs(b.createdAt));
  return { template: defaults[0] ?? null, ignored: defaults.slice(1) };
}

/** Whether a print area carries both millimetre values. */
export function hasPrintAreaMm(
  area: PrintAreaMmInput | null | undefined
): boolean {
  return (
    !!area &&
    isFinitePositive(area.maxWidthMm) &&
    isFinitePositive(area.maxHeightMm)
  );
}

/**
 * Whether a position is configured (R31): both millimetre values on its
 * print area and a calibrated default template.
 */
export function isPositionConfigured(
  area: PrintAreaMmInput | null | undefined,
  defaultTemplate: CalibrationFields | null | undefined
): boolean {
  return (
    hasPrintAreaMm(area) && !!defaultTemplate && isCalibrated(defaultTemplate)
  );
}

/** The position fields the selectability rule needs. */
export interface SelectablePositionCandidate {
  slug: string;
  isDefault: boolean;
  printArea?: PrintAreaMmInput | null;
}

/** The template fields the selectability rule needs. */
export type SelectableTemplateCandidate = DefaultTemplateCandidate &
  CalibrationFields & { positionKey: string };

/**
 * The default position of a product: the flagged one, else the first of
 * the given positions (callers pass the active positions in sort order,
 * the same fallback the Creator uses).
 */
export function defaultPositionOf<P extends { isDefault: boolean }>(
  positions: readonly P[]
): P | null {
  return (
    positions.find((position) => position.isDefault) ?? positions[0] ?? null
  );
}

/**
 * Whether sellers and Direct Sale may pick the product (R13): its default
 * position is configured (R31). `positions` are the active positions in
 * sort order, `templates` all templates of the product whatever their
 * status.
 */
export function isProductSelectable(
  positions: readonly SelectablePositionCandidate[],
  templates: readonly SelectableTemplateCandidate[]
): boolean {
  const position = defaultPositionOf(positions);
  if (!position) return false;
  const { template } = effectiveDefaultTemplate(
    templates.filter((t) => t.positionKey === position.slug)
  );
  return isPositionConfigured(position.printArea, template);
}

/**
 * A template's calibration as the admin walkthrough and the frame-review
 * queue show it: calibrated against both millimetres, a stored draft to
 * review (`inconsistent`, R29), or nothing usable yet.
 */
export type FrameCalibrationStatus =
  /** No usable calibration, or the position lacks its millimetres (R9). */
  | 'uncalibrated'
  /** A stored draft waits for review (NEEDS_REVIEW). */
  | 'inconsistent'
  /** CALIBRATED with a usable pixel scale on a position with both mm. */
  | 'calibrated';

/**
 * The frame status of one template, read from its stored calibration (R4).
 * A template whose position is missing or has no millimetres cannot be
 * calibrated (R9) and counts as uncalibrated whatever it stores.
 */
export function templateFrameStatus(
  area: PrintAreaMmInput | null | undefined,
  template: CalibrationFields
): FrameCalibrationStatus {
  if (!hasPrintAreaMm(area)) return 'uncalibrated';
  if (isCalibrated(template)) return 'calibrated';
  return template.calibrationStatus === 'NEEDS_REVIEW'
    ? 'inconsistent'
    : 'uncalibrated';
}

/** Severity order: the worst status of a set decides the product's status. */
const FRAME_STATUS_SEVERITY: Record<FrameCalibrationStatus, number> = {
  calibrated: 0,
  inconsistent: 1,
  uncalibrated: 2,
};

/**
 * The worst status of several templates, so a product with one
 * uncalibrated default template counts as uncalibrated. Without any status
 * (no default template at all) nothing has been calibrated.
 */
export function worstCalibrationStatus(
  statuses: readonly FrameCalibrationStatus[]
): FrameCalibrationStatus {
  if (statuses.length === 0) return 'uncalibrated';
  let worst: FrameCalibrationStatus = 'calibrated';
  for (const status of statuses) {
    if (FRAME_STATUS_SEVERITY[status] > FRAME_STATUS_SEVERITY[worst]) {
      worst = status;
    }
  }
  return worst;
}
